// fichier : mv-modal-quick.js

/**
 * Classe principale qui gère tout le système de vue rapide (Quick View)
 * Elle s'occupe de l'ouverture du modal, du chargement AJAX du produit,
 * et de la gestion des événements de fermeture
 */
class MvQuickView {
  constructor() {
    // Initialisation des propriétés qui stockeront les éléments DOM
    this.modal = null;
    this.loadingEl = null;
    this.contentEl = null;
    this.init();
  }

  /**
   * Point d'entrée de la classe - appelé automatiquement à la construction
   */
  init() {
    // Attache les événements de clic sur tous les boutons quick-view de la page
    this.attachButtonEvents();
  }

  /**
   * Utilise la délégation d'événements pour gérer tous les clics sur les boutons quick-view
   * Avantage : fonctionne même pour les boutons ajoutés dynamiquement après le chargement
   */
  attachButtonEvents() {
    document.addEventListener('click', (e) => {
      // Cherche le bouton quick-view le plus proche du clic (même si on clique sur le SVG à l'intérieur)
      const btn = e.target.closest('.quick-view-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        
        // Récupère le handle du produit depuis l'attribut data-product-handle
        const productHandle = btn.dataset.productHandle;
        if (productHandle) {
          this.openModal(productHandle);
        }
      }
    });
  }

  /**
   * Ouvre le modal et déclenche le chargement du produit via AJAX
   * @param {string} productHandle - Le handle unique du produit (ex: "beurre-bio-250g")
   */
  async openModal(productHandle) {
    // Récupère les éléments DOM du modal (fait une seule fois par ouverture)
    this.modal = document.getElementById('quick-view-modal');
    this.loadingEl = this.modal.querySelector('.quick-view-loading');
    this.contentEl = this.modal.querySelector('.quick-view-product-info');

    // Sécurité : si le modal n'existe pas dans le DOM, on arrête
    if (!this.modal) {
      console.error('Modal quick-view introuvable');
      return;
    }

    // Affiche le modal (passage de display: none à display: flex)
    this.modal.style.display = 'flex';
    // Ajoute une classe au body pour bloquer le scroll de la page
    document.body.classList.add('quick-view-open');

    // Affiche le loader et cache le contenu pendant le chargement
    this.loadingEl.style.display = 'block';
    this.contentEl.style.display = 'none';
    this.contentEl.innerHTML = '';

    try {
      // Lance la requête AJAX pour charger le produit
      await this.loadProduct(productHandle);
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      // En cas d'erreur, affiche un message d'erreur à l'utilisateur
      this.showError();
    }

    // Attache les événements pour fermer le modal (clic overlay, bouton X, touche Escape)
    this.attachCloseEvents();
  }

  /**
   * Charge le contenu du produit via AJAX et l'injecte dans le modal
   * @param {string} productHandle - Le handle du produit
   */
  async loadProduct(productHandle) {
    // Construit l'URL avec le paramètre ?view=quick-view
    // Shopify utilisera le template product.quick-view.liquid au lieu du template par défaut
    const url = `/products/${productHandle}?view=quick-view`;

    console.log('Chargement du produit:', url); // DEBUG

    // Fait la requête AJAX vers Shopify
    const response = await fetch(url);
    
    // Vérifie que la requête a réussi (statut 200)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Récupère le HTML retourné par Shopify
    const html = await response.text();
    
    console.log('HTML reçu:', html.substring(0, 200)); // DEBUG - Affiche les 200 premiers caractères

    // Parse le HTML avec DOMParser pour pouvoir le manipuler
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Cherche l'élément .quick-view-grid dans le HTML retourné
    // Cet élément est défini dans product.quick-view.liquid
    const productContent = doc.querySelector('.quick-view-grid');

    console.log('Contenu trouvé:', productContent); // DEBUG

    if (productContent) {
      // Injecte le contenu du produit dans le modal
      this.contentEl.innerHTML = productContent.outerHTML;
      
      // Masque le loader et affiche le contenu
      this.loadingEl.style.display = 'none';
      this.contentEl.style.display = 'block';
      
      // ✅ CRITIQUE : Initialise les dropdowns après l'injection du HTML
      // Sans cette étape, les sections déroulantes ne fonctionneraient pas
      if (typeof window.initQuickViewDropdowns === 'function') {
        window.initQuickViewDropdowns();
      } else {
        console.error('❌ La fonction initQuickViewDropdowns n\'est pas définie');
      }
    } else {
      // Si .quick-view-grid n'est pas trouvé, affiche le HTML complet pour debug
      console.error('Sélecteur .quick-view-grid introuvable dans:', doc.body.innerHTML);
      throw new Error('Contenu du produit introuvable - Vérifier que product.quick-view.liquid existe');
    }
  }

  /**
   * Affiche un message d'erreur utilisateur en cas de problème de chargement
   */
  showError() {
    this.contentEl.innerHTML = `
      <div style="text-align: center; padding: 40px;">
        <p style="color: #e74c3c; font-size: 16px; margin-bottom: 10px;">
          Une erreur est survenue lors du chargement du produit.
        </p>
        <p style="color: #666; font-size: 14px;">
          Veuillez réessayer ou visiter la page produit directement.
        </p>
      </div>
    `;
    
    // Masque le loader et affiche le message d'erreur
    this.loadingEl.style.display = 'none';
    this.contentEl.style.display = 'block';
  }

  /**
   * Attache les trois façons de fermer le modal :
   * 1. Clic sur le bouton X
   * 2. Clic sur l'overlay (fond sombre)
   * 3. Touche Escape du clavier
   */
  attachCloseEvents() {
    const closeBtn = this.modal.querySelector('.quick-view-close');
    const overlay = this.modal.querySelector('.quick-view-overlay');

    // Fonction de fermeture réutilisable
    const closeModal = () => {
      this.closeModal();
    };

    // Clic sur le bouton X
    if (closeBtn) {
      closeBtn.onclick = closeModal;
    }

    // Clic sur l'overlay (fond sombre autour du modal)
    if (overlay) {
      overlay.onclick = closeModal;
    }

    // Touche Escape - avec suppression de l'événement après fermeture
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        // Nettoie l'événement pour éviter les fuites mémoire
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
  }

  /**
   * Ferme le modal et nettoie le contenu
   */
  closeModal() {
    if (this.modal) {
      // Cache le modal
      this.modal.style.display = 'none';
      // Réactive le scroll de la page
      document.body.classList.remove('quick-view-open');
      
      // Vide le contenu pour libérer la mémoire
      if (this.contentEl) {
        this.contentEl.innerHTML = '';
      }
    }
  }
}

// Initialisation automatique au chargement de la page
// Vérifie d'abord si le DOM est déjà chargé ou en cours de chargement
if (document.readyState === 'loading') {
  // Si en cours de chargement, attendre l'événement DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    new MvQuickView();
  });
} else {
  // Si déjà chargé (script defer ou async), initialiser directement
  new MvQuickView();
}
